import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { usePatchJob } from "../../../clients/hooks/usePatchJob";

type JobDescriptionCardProps = {
  jobId: string | number;
  description?: string | null;
  onUpdated?: () => void;
};

export function JobDescriptionCard({ jobId, description, onUpdated }: JobDescriptionCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(description ?? "");

  const { mutateAsync: patchJob, isPending } = usePatchJob();

  useEffect(() => {
    setValue(description ?? "");
  }, [description]);

  const cleanDescription = value.trim();

  const handleCancel = () => {
    setValue(description ?? "");
    setIsEditing(false);
  };

  const handleSave = async () => {
    await patchJob({
      jobId,
      payload: {
        description: cleanDescription || null,
      },
    });

    setIsEditing(false);
    onUpdated?.();
  };

  return (
    <Box
      sx={{
        px: 2.5,
        py: 2,
        mx: 3,
        borderRadius: 3,
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1.25,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          Job Description
        </Typography>

        {isEditing ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Save">
              <IconButton size="small" onClick={handleSave} disabled={isPending}>
                <CheckIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Cancel">
              <IconButton size="small" onClick={handleCancel} disabled={isPending}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Tooltip title={cleanDescription ? "Edit description" : "Add description"}>
            <IconButton size="small" onClick={() => setIsEditing(true)}>
              <EditOutlinedIcon sx={{ fontSize: 18, color: "#5681d9" }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {isEditing ? (
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={4}
          maxRows={8}
          size="small"
          placeholder="Write a description for this job"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              void handleSave();
            }

            if (event.key === "Escape") {
              handleCancel();
            }
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              fontSize: 14,
            },
          }}
        />
      ) : cleanDescription ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            lineHeight: 1.6,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {cleanDescription}
        </Typography>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontStyle: "italic",
          }}
        >
          No description for this job.
        </Typography>
      )}
    </Box>
  );
}
